// =============================================================
// קובץ: C:\gocode\projects\cartax\git\cartax_fe\src\services\backoffice\epaDataService.ts
// =============================================================

import { handleSessionError } from './authService';
import { API_BASE_URL } from '../../config/api';

// ============== Interfaces ==============

export interface EpaYearInfo {
  model_year: number;
  record_count: number;
  makes_count: number;
  upload_date?: string;
}

export interface EpaStatsData {
  years: EpaYearInfo[];
  total_records: number;
  total_makes: number;
}

export interface EpaUploadResult {
  inserted_count: number;
  updated_count: number;
  skipped_count: number;
  deleted_year?: number;
  message: string;
}

export interface ApiResponse<T> {
  res: 'OK' | 'NOK';
  msg: string;
  data: T;
  error_code?: string;
}

// ============== Helper Functions ==============

const createFormData = (data: Record<string, any>): FormData => {
  const formData = new FormData();

  const sessionToken = localStorage.getItem('backoffice_session_id');
  if (sessionToken) {
    formData.append('session_token', sessionToken);
  }

  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value.toString());
    }
  });
  return formData;
};

const handleApiResponse = async (response: Response): Promise<any> => {
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const result = await response.json();

  if (handleSessionError(result)) {
    throw new Error('Session expired - redirecting to login');
  }

  return result;
};

// ============== Service Class ==============

class EpaDataService {
  /**
   * קבלת רשימת שנים זמינות
   */
  async getEpaYears(): Promise<number[]> {
    try {
      const formData = createFormData({});

      const response = await fetch(`${API_BASE_URL}/Api/service/backoffice/epa_get_years`, {
        method: 'POST',
        body: formData
      });

      const result = await handleApiResponse(response);

      if (result.res === 'NOK') {
        throw new Error(result.msg || 'Failed to fetch EPA years');
      }

      return result.data.years || [];

    } catch (error) {
      console.error('EPA years service error:', error);
      throw error;
    }
  }

  /**
   * קבלת סטטיסטיקות מלאות (שנים + מספר רשומות + יצרנים)
   */
  async getEpaStats(): Promise<EpaStatsData> {
    try {
      const formData = createFormData({});

      const response = await fetch(`${API_BASE_URL}/Api/service/backoffice/epa_get_stats`, {
        method: 'POST',
        body: formData
      });

      const result = await handleApiResponse(response);

      if (result.res === 'NOK') {
        throw new Error(result.msg || 'Failed to fetch EPA statistics');
      }

      return result.data;

    } catch (error) {
      console.error('EPA stats service error:', error);
      throw error;
    }
  }

  /**
   * העלאת קובץ Excel של EPA
   * @param file - קובץ Excel
   * @param targetYear - שנת היעד (אופציונלי - אם לא מועבר, לוקח מהקובץ)
   */
  async uploadEpaFile(file: File, targetYear?: number): Promise<EpaUploadResult> {
    try {
      const sessionToken = localStorage.getItem('backoffice_session_id');

      if (!sessionToken) {
        throw new Error('No session token found');
      }

      const formData = new FormData();
      formData.append('session_token', sessionToken);
      formData.append('file', file);

      if (targetYear) {
        formData.append('target_year', targetYear.toString());
      }

      // console.log('Uploading EPA file...', file.name);

      const response = await fetch(`${API_BASE_URL}/Api/service/backoffice/epa_upload`, {
        method: 'POST',
        body: formData
      });

      const result = await handleApiResponse(response);

      if (result.res === 'NOK') {
        throw new Error(result.msg || 'Failed to upload EPA file');
      }

      return result.data;

    } catch (error) {
      console.error('EPA upload service error:', error);
      throw error;
    }
  }

  /**
   * מחיקת שנה מה-DB
   * @param year - השנה למחיקה
   */
  async deleteEpaYear(year: number): Promise<{ deleted_count: number }> {
    try {
      const formData = createFormData({ year });

      const response = await fetch(`${API_BASE_URL}/Api/service/backoffice/epa_delete_year`, {
        method: 'POST',
        body: formData
      });

      const result = await handleApiResponse(response);

      if (result.res === 'NOK') {
        throw new Error(result.msg || 'Failed to delete EPA year');
      }

      return result.data;

    } catch (error) {
      console.error('EPA delete year service error:', error);
      throw error;
    }
  }
}

export const epaDataService = new EpaDataService();